import React, { useState } from 'react';
import { bannerMeta } from '../data/bannerMeta';
import { bannerSeriesMap } from '../data/bannerPools';
import rates from '../data/rates.json';
import { rarityColors } from '../utils/rarityColors';
import confetti from 'canvas-confetti';
import '../App.css';
import { supabase } from '../supabaseClient';
import { getAnimationSrc, getFigurineImageSrc } from '../utils/figurineMedia';

export default function SummonPage() {
  const [selectedBanner, setSelectedBanner] = useState('All-characters-mystery-banner');
  const [result, setResult] = useState(null);
  const [isSummoning, setIsSummoning] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Multi state
  const [isMulti, setIsMulti] = useState(false);
  const [multiResults, setMultiResults] = useState([]);
  const [multiTotal, setMultiTotal] = useState(0);
  const [multiIndex, setMultiIndex] = useState(0); // 1-based indicator
  const [revealPhase, setRevealPhase] = useState('image');

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const fetchFigurinesByRarity = async (rarity, seriesKeywords = []) => {
    const { data, error } = await supabase
      .from('figurines')
      .select('*')
      .eq('rating', parseInt(rarity))
      .gt('stock', 0);

    if (error) {
      console.error('Error fetching from Supabase:', error);
      return [];
    }
    if (seriesKeywords.length === 0) return data || [];

    return (data || []).filter((fig) =>
      seriesKeywords.some((keyword) =>
        fig.series.toLowerCase().includes(keyword.toLowerCase())
      )
    );
  };

  const rollRarity = (rateMap) => {
    const roll = Math.random();
    let cumulative = 0;
    for (let rarity in rateMap) {
      cumulative += rateMap[rarity];
      if (roll < cumulative) return rarity;
    }
    return '2';
  };

  const rollOnce = async (rateMap, seriesKeywords) => {
    const rarity = rollRarity(rateMap);
    const options = await fetchFigurinesByRarity(rarity, seriesKeywords);
    if (!options || options.length === 0) return null;
    const chosen = options[Math.floor(Math.random() * options.length)];
    return { ...chosen, rating: rarity };
  };

  // Core summon used by both single & multi:
  // - shows animation
  // - optionally plays banner sound
  // - optionally updates single-result UI
  const doSummon = async ({ playBannerSound = true, updateSingleUI = false }) => {
    setIsSummoning(true);
    if (playBannerSound) {
      new Audio(bannerMeta[selectedBanner].sound).play();
    }

    // let the animation breathe
    await sleep(1200);

    const rateMap = rates[selectedBanner];
    const seriesKeywords = bannerSeriesMap[selectedBanner] || [];
    const rolled = await rollOnce(rateMap, seriesKeywords);

    // hide animation
    setIsSummoning(false);

    if (!rolled) return null;

    if (getAnimationSrc(rolled)) {
      // put the figure into result so the overlay can read result.animation
      setResult(rolled);
      setRevealPhase('anim');        // show GIF
      await sleep(2300);             // wait ~3.2s (tweak if you want)
      setRevealPhase('image');       // then show the normal result card
    } else {
      setRevealPhase('image');       // straight to card if no GIF
    }


    // per-result SFX
    const rarity = rolled.rating;
    const nameLower = rolled.name.toLowerCase();
    let rarityAudio;

    if (nameLower.includes('gogeta')) {
      rarityAudio = new Audio('/sounds/fusion.mp3');
    } else if (rarity === '4' || rarity === '5') {
      const raritySound = bannerMeta[selectedBanner]?.raritySounds?.[rarity];
      rarityAudio = new Audio(raritySound || `/sounds/rarity-${rarity}.mp3`);
    } else if (rarity === '6') {
      const raritySound = bannerMeta[selectedBanner]?.raritySounds?.[rarity];
      rarityAudio = new Audio(raritySound || `/sounds/rarity-5.mp3`);
    } else {
      rarityAudio = new Audio('/sounds/common.mp3');
    }
    rarityAudio.play();

    // Confetti rules
    if (updateSingleUI) {
      if (rarity === '3') {
        confetti({ particleCount: 1500, spread: 1000, origin: { y: 0.5 } });
      }
    } else {
      // small pop on multi for 4★+
      if (['4', '5', '6'].includes(String(rarity))) {
        confetti({ particleCount: 300, spread: 500, origin: { y: 0.5 } });
      }
    }

    if (updateSingleUI) {
      if (rarity === '4' || rarity === '5' || rarity === '6') {
        setIsTransitioning(true);
        await sleep(1500);
        setIsTransitioning(false);
      }

      const timestamp = new Date().toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });

      setResult(rolled);
      setHistory((prev) => [...prev, { ...rolled, time: timestamp }]);
    }

    return rolled;
  };

  // Single summon
  const handleSummon = async () => {
    setIsMulti(false);
    setResult(null);
    await doSummon({ playBannerSound: true, updateSingleUI: true });
  };

  // Multi summon x3 (fixed)
  const handleMultiSummon = async () => {
    const count = 3; // do not change per your requirement
    setIsMulti(true);
    setResult(null);
    setMultiResults([]);
    setMultiTotal(count);
    setMultiIndex(0);

    // play banner sound once at the beginning
    new Audio(bannerMeta[selectedBanner].sound).play();

    const pulls = [];
    for (let i = 0; i < count; i++) {
      setMultiIndex(i + 1);
      // reuse same summon flow; don't update single UI and don't replay banner sound
      // eslint-disable-next-line no-await-in-loop
      const rolled = await doSummon({ playBannerSound: false, updateSingleUI: true });
      if (rolled) {
        pulls.push(rolled);
        setMultiResults((prev) => [...prev, rolled]);
      }
      // small beat between reveals
      // eslint-disable-next-line no-await-in-loop
      await sleep(2500);
    }

    if (pulls.length) {
      const timestamp = new Date().toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      setHistory((prev) => [...prev, ...pulls.map((p) => ({ ...p, time: timestamp }))]);
    } else {
      alert('No available items for this banner right now.');
    }
  };

  const banner = bannerMeta[selectedBanner] || {
    name: 'Unknown Banner',
    image: '',
    color: '#333',
  };

  const bannerNames = Object.keys(bannerMeta);

  return (
    <div className="nfig-page summon-page">
      <div className="pointer-events-none absolute left-1/2 top-44 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
      <main className="nfig-container flex flex-col items-center">
        <header className="mb-9 text-center">
          <p className="nfig-kicker mb-3">召喚 / Test your fate</p>
          <h1 className="nfig-page-title">Mystery Summon</h1>
          <p className="mx-auto mt-4 max-w-xl text-slate-400">Choose a banner, activate the portal, and reveal the figure waiting on the other side.</p>
        </header>

        <section className="nfig-panel w-full max-w-4xl overflow-hidden rounded-2xl">
          <div className="grid lg:grid-cols-[1fr_340px]">
            <div className="relative min-h-[300px] overflow-hidden bg-[#080c18] sm:min-h-[430px]">
              <img src={banner.image} alt={banner.name} className="absolute inset-0 h-full w-full object-contain p-3 sm:p-5" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1020] via-transparent to-black/20" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <p className="nfig-kicker">Active banner</p>
                <h2 className="mt-2 text-3xl font-black uppercase text-white sm:text-4xl">{banner.name}</h2>
              </div>
            </div>

            <div className="flex flex-col justify-center bg-[#141b2a] p-6 sm:p-8">
              <label className="mb-6 block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-300">Select banner</span>
                <select
                  value={selectedBanner}
                  onChange={(e) => {
                    setSelectedBanner(e.target.value);
                    setResult(null);
                  }}
                  className="nfig-input cursor-pointer"
                >
                  {bannerNames.map((key) => (
                    <option key={key} value={key}>{bannerMeta[key].name}</option>
                  ))}
                </select>
              </label>

              <p className="mb-5 text-sm leading-relaxed text-slate-400">Every pull is selected from available in-stock figures in this banner's series pool.</p>

              <div className="flex flex-col gap-3">
          <button
            onClick={handleSummon}
                  className="summon-cta"
          >
                  <span className="summon-cta__spark" aria-hidden="true">◆</span>
                  Single Summon
          </button>

          <button
            onClick={handleMultiSummon}
                  className="nfig-button-secondary w-full"
          >
                  Multi Summon x3
          </button>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 flex min-h-11 flex-wrap justify-center gap-3">
          {history.length > 0 && (
            <>
              <button onClick={() => setShowHistory((prev) => !prev)} className="nfig-button-secondary">
                {showHistory ? 'Hide History' : 'Show History'}
              </button>
              <button
                onClick={() => {
                  setHistory([]);
                  setShowHistory(false);
                }}
                className="nfig-button-danger"
              >
                Clear History
              </button>
            </>
          )}
        </div>

        {showHistory && (
          <section className="nfig-panel mt-4 max-h-96 w-full max-w-2xl overflow-y-auto rounded-xl p-5">
            <h3 className="mb-4 text-xl font-black uppercase tracking-wide text-white">Summon History</h3>
            <ul className="space-y-2 text-sm">
              {history.map((item, i) => (
                <li key={i} className="rounded-lg border border-white/5 bg-black/20 p-3 text-slate-300">
                  <span style={{ color: rarityColors[item.rating], fontWeight: 'bold' }}>
                    ★ {item.rating}
                  </span>{' '}
                  — {item.name} ({item.series})<br />
                  <span className="text-xs text-slate-500">{item.time}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

      {isSummoning && (
        <div className="summon-overlay">
          {/* progress badge for multi */}
          {isMulti && multiTotal > 0 && (
            <div className="absolute right-4 top-4 z-20 rounded-full border border-blue-300/30 bg-black/70 px-4 py-2 text-sm font-bold text-white">
              Pull {multiIndex} / {multiTotal}
            </div>
          )}
        <img src={bannerMeta[selectedBanner].animation} alt="Summoning..." />
        </div>
      )}

      {isTransitioning && <div className="fixed inset-0 bg-black z-[9999]"></div>}

      {result && !isSummoning && (
            <div
              className="result-overlay"
              onClick={() => {
                // prevent closing while GIF is playing
                if (revealPhase === 'image') setResult(null);
              }}
            >
              {revealPhase === 'anim' && getAnimationSrc(result) ? (
                // --- ANIMATION PHASE (GIF) ---
                <img
                  src={getAnimationSrc(result)}
                  alt="Summoning animation"
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : (
                // --- IMAGE PHASE (your existing card) ---
                <>
                  <p className="nfig-kicker mb-2">召喚成功 / Summon complete</p>
                  <h2 className="mb-2 text-3xl font-black uppercase text-white">You Got</h2>
                  <p className="text-xl font-semibold text-slate-200">
                    {result.name} ({result.series})
                  </p>
                  <p className="mt-2 text-lg font-black text-[#f5c451]">★ {result.rating === 6 ? 5 : result.rating}</p>
                  <img
                    src={getFigurineImageSrc(result)}
                    alt={result.name}
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                  <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Click anywhere to close</p>
                </>
              )}
            </div>
          )}


      {/* MULTI RESULT GRID */}
      {isMulti && multiResults.length > 0 && !isSummoning && (
        <section className="nfig-panel mt-6 w-full max-w-4xl rounded-xl p-5">
          <p className="nfig-kicker mb-1">Pull results</p>
          <h3 className="mb-4 text-xl font-black uppercase text-white">Multi Summon Results</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {multiResults.map((item, idx) => (
              <article key={`${item.id}-${idx}`} className="overflow-hidden rounded-lg border border-blue-400/20 bg-[#0b1020] p-3 shadow">
                <div className="text-sm font-bold" style={{ color: rarityColors[item.rating] }}>
                  ★ {item.rating === 6 ? 5 : item.rating}
                </div>
                <div className="my-2 flex h-32 items-center justify-center rounded bg-slate-100 p-2">
                  <img src={getFigurineImageSrc(item)} alt={item.name} className="h-full w-full object-contain" />
                </div>
                <div className="mt-1 text-sm font-bold text-white">{item.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-blue-300">{item.series}</div>
              </article>
            ))}
          </div>
        </section>
      )}
      </main>
    </div>
  );
}
